import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import { toObjectMap } from "@@/utilities/dictionary";
import type {
    MinMaxRange} from "@@/utilities/set-within-range";
import {
    minMaxRangeZodSchema,
} from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    type MetaContent,
    metaContentZodSchema,
} from "../meta-content/meta-content";
import type { Optional} from "./optional";
import { optionalZodSchema } from "./optional";
import type {
    OptionalGroupId} from "./optional-group-id";
import {
    optionalGroupIdFactory,
    optionalGroupIdZodSchema,
} from "./optional-group-id";
import type {
    OptionalPercentage} from "./optional-percentage";
import {
    optionalPercentageZodSchema,
} from "./optional-percentage";

const optionalGroupTypes = ["SINGLE", "MULTI"] as const;
type OptionalGroupType = (typeof optionalGroupTypes)[number];
const optionalGroupTypesMap = toObjectMap<OptionalGroupType>([
    ...optionalGroupTypes,
]);

type OptionalGroup = {
    readonly _id: OptionalGroupId;
    readonly _type: "OPTIONAL_GROUP";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly meta: MetaContent;
    readonly optionals: (Optional | OptionalPercentage)[];
    readonly minMax: MinMaxRange;
    readonly free: number;
    readonly providerId: ProviderId;
};

type FactoryParams = Except<OptionalGroup, "_id" | "_type" | "_createdAt">;
const optionalGroupFactory = (
    params: FactoryParams & { _id?: OptionalGroupId },
): OptionalGroup => {
    return {
        ...params,
        _id: params._id || optionalGroupIdFactory(),
        _type: "OPTIONAL_GROUP",
        _createdAt: nowIsoAndUnix(),
    };
};

const optionalGroupZodSchema: z.ZodSchema<OptionalGroup> = z.object({
    _id: optionalGroupIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("OPTIONAL_GROUP"),
    minMax: minMaxRangeZodSchema,
    meta: metaContentZodSchema,
    providerId: providerIdZodSchema,
    free: z.number().int(),
    optionals: z.array(
        z.union([optionalZodSchema, optionalPercentageZodSchema]),
    ),
});

export {
    optionalGroupFactory,
    optionalGroupTypes,
    optionalGroupTypesMap,
    optionalGroupZodSchema,
};
export type { OptionalGroup, OptionalGroupType };
